import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@/components/ui/button';
import { Upload, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { layoutPhotoService, LayoutPhotoUpload } from '@/services/layoutPhotoService';

interface MigrationLayoutPhotoUploadProps {
  sequenceId: string;
  maxPhotos?: number;
  disabled?: boolean;
}

const MigrationLayoutPhotoUpload: React.FC<MigrationLayoutPhotoUploadProps> = ({
  sequenceId,
  maxPhotos = 3,
  disabled = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (disabled) return;

      if (acceptedFiles.length > maxPhotos) {
        toast.warning(`Only uploading ${maxPhotos} photos (maximum ${maxPhotos} allowed)`);
      }

      const filesToAdd = acceptedFiles.slice(0, maxPhotos);

      // Automatically upload the photos
      setIsUploading(true);
      try {
        const photosToUpload: LayoutPhotoUpload[] = filesToAdd.map((file) => ({
          file,
          caption: '',
        }));

        await layoutPhotoService.uploadLayoutPhotos(sequenceId, photosToUpload);
        
        toast.success(`Successfully uploaded ${filesToAdd.length} layout photo(s)`);
        
      } catch (error) {
        console.error('Failed to upload layout photos:', error);
        toast.error('Failed to upload layout photos. Please try again.');
      } finally {
        setIsUploading(false);
      }
    },
    [sequenceId, maxPhotos, disabled]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
    },
    maxFiles: maxPhotos,
    maxSize: 5 * 1024 * 1024, // 5MB
    disabled,
    noClick: false, // Allow clicking on the root div
    onDropRejected: (rejectedFiles) => {
      rejectedFiles.forEach((rejection) => {
        const errors = rejection.errors.map((error) => {
          switch (error.code) {
            case 'file-too-large':
              return 'File is too large (max 5MB)';
            case 'file-invalid-type':
              return 'Invalid file type (only JPG, PNG, GIF allowed)';
            default:
              return error.message;
          }
        });
        toast.error(errors.join(', '));
      });
    },
  });



  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium flex items-center gap-2">
        <Camera className="h-4 w-4" />
        Layout Photos (Optional)
      </h4>

      <div className="p-3 bg-blue-50 rounded-lg">
        <p className="text-xs text-blue-800">
          Upload up to {maxPhotos} photos showing your xLights or Light-O-Rama layout.
          These help buyers visualize how your sequence looks on an actual display.
        </p>
      </div>

      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
          ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center space-y-2">
          <Camera className="h-8 w-8 text-gray-400" />
          <p className="text-sm text-gray-600">
            {isUploading
              ? 'Uploading photos...'
              : isDragActive
              ? 'Drop photos here...'
              : 'Drag & drop layout photos here, or click to select'}
          </p>
          <p className="text-xs text-gray-500">
            Up to {maxPhotos} photos • JPG, PNG, GIF • Max 5MB each
          </p>
          {!isUploading && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
            >
              <Upload className="h-4 w-4 mr-2" />
              Choose Photos
            </Button>
          )}
        </div>
      </div>

    </div>
  );
};

export default MigrationLayoutPhotoUpload;
